/**
 * 配额体系 · 网页端。
 *
 * 配额规则本身没变，仍由数据库里的 check_quota / record_usage 决定
 * （见 migrations_sql/01b_functions.sql），这里只换成 HTTP 调用。
 *
 * 约定：**先查后做、做成才记** —— 动作失败不消耗配额。这个顺序由
 * 调用方保证（TestApp.saveToCloud、分享弹窗），本模块不自动记账。
 */
import { api } from './api'

export type QuotaAction = 'create_score' | 'vlm_recognize' | 'create_share'

export type QuotaCheck = {
	allowed: boolean
	used: number
	limit: number
	remaining: number
}

export async function checkQuota(action: QuotaAction): Promise<QuotaCheck> {
	try {
		return await api.get<QuotaCheck>(`/quota/check?action=${action}`)
	} catch (err) {
		// 查不到就放行，让真实写库报错兜底 —— 不把用户锁死。
		// ⚠️ 后端在 score_create 里**还会再查一次**并拒绝超额写入，
		//    所以这里的"放行"只是不弹无谓的错误提示，不是真的能绕过。
		console.warn('check_quota 失败，放行：', err)
		return {allowed: true, used: 0, limit: 0, remaining: 0}
	}
}

export async function recordUsage(action: QuotaAction, amount = 1): Promise<void> {
	try {
		await api.post('/quota/record', {action, amount})
	} catch (err) {
		// 记账失败不影响主流程（曲谱已经存下来了），
		// 只记日志 —— 让它抛出去会中断"保存成功"的提示。
		console.warn('record_usage 失败：', err)
	}
}

/**
 * 用户可读的配额文案。
 *
 * 不在这里写死上限数字（原来写的是「免费版可保留 20 首」）—— 上限在 plans 表里，
 * 后台调了之后文案就成了假话。要说的数字都已由 `check.used/limit` 带出来；
 * 这里只说"怎么解决"。Plus 的具体额度见库页的「5/200」与升级弹窗对比表。
 */
export function quotaMessage(action: QuotaAction, check: QuotaCheck): string {
	const names: {[_ in QuotaAction]: string} = {
		create_score: '云端曲谱',
		vlm_recognize: 'AI 曲谱识别',
		create_share: '分享链接',
	}
	const unit: {[_ in QuotaAction]: string} = {
		create_score: '首',
		vlm_recognize: '次/月',
		create_share: '条/月',
	}
	const advice = action === 'create_score'
		? '删掉不需要的曲谱会立刻释放名额，也可以升级 Plus 会员扩容。'
		: '升级 Plus 会员可获得更高额度。'
	return `${names[action]}额度已用完（${check.used}/${check.limit}${unit[action]}）。${advice}`
}